import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import ListaAnimales from './components/ListaAnimales';
import FichaAnimal from './components/FichaAnimal';
import ControlAsistencia from './components/ControlAsistencia';
import Usuarios from './components/Usuarios';
import Login from './Login';
import { getToken, getUsuario, logout, apiFetch } from './auth';
import API_CONFIG from './config';
import { Analytics } from '@vercel/analytics/react';
import './responsive.css'; // debe ir último para tener prioridad en el cascade

function App() {
  const [autenticado, setAutenticado]       = useState(!!getToken());
  const [usuario, setUsuario]               = useState(getUsuario());
  const [seccion, setSeccion]               = useState('rodeo');
  const [vista, setVista]                   = useState('lista');
  const [animalSeleccionado, setAnimalSeleccionado] = useState(null);

  // Menú de usuario
  const [menuAbierto, setMenuAbierto]       = useState(false);
  const menuRef                             = useRef(null);

  // Modal cambiar contraseña
  const [mostrarCambioPass, setMostrarCambioPass] = useState(false);
  const [formPass, setFormPass]             = useState({ passwordActual: '', passwordNueva: '', passwordConfirm: '' });
  const [errorPass, setErrorPass]           = useState('');
  const [exitoPass, setExitoPass]           = useState('');
  const [guardandoPass, setGuardandoPass]   = useState(false);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogin = (data) => {
    setAutenticado(true);
    setUsuario({ username: data.username, nombre: data.nombre, rol: data.rol });
  };

  const handleLogout = () => {
    logout();
    setAutenticado(false);
    setUsuario(null);
    setSeccion('rodeo');
    setVista('lista');
    setAnimalSeleccionado(null);
  };

  const abrirFicha = (animal) => { setAnimalSeleccionado(animal); setVista('ficha'); };
  const volverALista = () => { setAnimalSeleccionado(null); setVista('lista'); };
  const irSeccion = (s) => { setSeccion(s); setVista('lista'); setAnimalSeleccionado(null); };

  const cambiarPassword = async (e) => {
    e.preventDefault();
    setErrorPass('');
    if (formPass.passwordNueva !== formPass.passwordConfirm) {
      return setErrorPass('Las contraseñas nuevas no coinciden.');
    }
    setGuardandoPass(true);
    try {
      const res = await apiFetch(`${API_CONFIG.BASE_URL}/api/auth/cambiar-password`, {
        method: 'POST',
        body: JSON.stringify({ passwordActual: formPass.passwordActual, passwordNueva: formPass.passwordNueva }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setExitoPass('Contraseña actualizada correctamente.');
      setFormPass({ passwordActual: '', passwordNueva: '', passwordConfirm: '' });
      setTimeout(() => { setExitoPass(''); setMostrarCambioPass(false); }, 2000);
    } catch (e) { setErrorPass(e.message); }
    finally { setGuardandoPass(false); }
  };

  if (!autenticado) return <Login onLogin={handleLogin} />;

  return (
    <>
      <div className="app">
        <header className="app-header">
        <div className="header-logo">
          🐄 <span>Sistema Medalla Milagrosa</span>
        </div>
        <nav className="header-nav">
          <button className={seccion === 'rodeo' ? 'nav-btn active' : 'nav-btn'} onClick={() => irSeccion('rodeo')}>
            📋 Rodeo
          </button>
          <button className={seccion === 'asistencia' ? 'nav-btn active' : 'nav-btn'} onClick={() => irSeccion('asistencia')}>
            ✅ Asistencia
          </button>
          {usuario?.rol === 'ADMIN' && (
            <button className={seccion === 'usuarios' ? 'nav-btn active' : 'nav-btn'} onClick={() => irSeccion('usuarios')}>
              👥 Usuarios
            </button>
          )}
        </nav>

        {/* Menú de usuario */}
        <div className="header-user" ref={menuRef}>
          <button className="user-menu-btn" onClick={() => setMenuAbierto(p => !p)}>
            👤 {usuario?.nombre} ▾
          </button>
          {menuAbierto && (
            <div className="user-dropdown">
              <div className="user-dropdown-info">
                <strong>{usuario?.nombre}</strong>
                <small>{usuario?.username} · {usuario?.rol}</small>
              </div>
              <hr />
              <button className="user-dropdown-item" onClick={() => { setMostrarCambioPass(true); setMenuAbierto(false); }}>
                🔑 Cambiar Contraseña
              </button>
              <button className="user-dropdown-item danger" onClick={handleLogout}>
                🚪 Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="app-main">
        {seccion === 'rodeo' && vista === 'lista' && <ListaAnimales onVerFicha={abrirFicha} />}
        {seccion === 'rodeo' && vista === 'ficha' && (
          <FichaAnimal animalId={animalSeleccionado?.id} onVolver={volverALista} onVerCria={abrirFicha} />
        )}
        {seccion === 'asistencia' && (
          <>
            <div style={{ display: vista === 'ficha' ? 'none' : 'block' }}>
              <ControlAsistencia onVerFicha={(animal) => { setAnimalSeleccionado(animal); setVista('ficha'); }} />
            </div>
            {vista === 'ficha' && (
              <FichaAnimal animalId={animalSeleccionado?.id}
                onVolver={() => { setAnimalSeleccionado(null); setVista('lista'); }}
                onVerCria={abrirFicha} modal={true} />
            )}
          </>
        )}
        {seccion === 'usuarios' && usuario?.rol === 'ADMIN' && <Usuarios />}
      </main>

      {/* Modal cambiar contraseña */}
      {mostrarCambioPass && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMostrarCambioPass(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>🔑 Cambiar Contraseña</h3>
              <button className="modal-close" onClick={() => setMostrarCambioPass(false)}>✕</button>
            </div>
            <form onSubmit={cambiarPassword} className="ficha-form">
              {errorPass && <div className="alert alert-danger">{errorPass}</div>}
              {exitoPass && <div className="alert alert-success">{exitoPass}</div>}
              <div className="form-group">
                <label>Contraseña actual *</label>
                <input type="password" value={formPass.passwordActual} required
                  onChange={e => setFormPass(p => ({ ...p, passwordActual: e.target.value }))}
                  placeholder="Tu contraseña actual" />
              </div>
              <div className="form-group">
                <label>Nueva contraseña *</label>
                <input type="password" value={formPass.passwordNueva} required
                  onChange={e => setFormPass(p => ({ ...p, passwordNueva: e.target.value }))}
                  placeholder="Mínimo 4 caracteres" />
              </div>
              <div className="form-group">
                <label>Confirmar nueva contraseña *</label>
                <input type="password" value={formPass.passwordConfirm} required
                  onChange={e => setFormPass(p => ({ ...p, passwordConfirm: e.target.value }))}
                  placeholder="Repetí la nueva contraseña" />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarCambioPass(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardandoPass}>
                  {guardandoPass ? 'Guardando...' : '✓ Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
      <Analytics />
    </>
  );
}

export default App;
